'use client';

import { FunctionComponent, useMemo } from 'react';

import { classes } from '@/next-utils/css-utils/scss-utils';
import { Image } from '@/react/components/core-ui/Image';
import { IImage } from '@/services/models/Media/Image';
import MediaService from '@/services/isomorphic/MediaService';
import ConfigurationService from '@/services/isomorphic/ConfigurationService';
import { IContentMediaItem } from '../../template-data/IContentMedia';
import { HoverManipulation } from './HoverManipulation';

import { getAspectRatioNumberFromEnum } from '../../utilities/aspectRatioUtils';
import { getMediaWidthFromElementWidth } from '../../utilities/getMediaWidthFromElementWidth';

import S from './styles.base.module.scss';

export interface IContentImageProps {
  /**
   * The array of links to be turned into buttons.
   */
  media: IContentMediaItem;

  /**
   * The current width of the image, this has been assessed in the ContentMedia component.
   */
  elementWidth: number;
  /** Whether or not the image should be loaded with high priority. */
  highPriority?: boolean;
}

/**
 * A component to render a content image.
 */
export const ContentImage: FunctionComponent<IContentImageProps> = ({
  media,
  elementWidth,
  highPriority
}) => {
  const metadata = media?.item?.context?.custom ?? { alt: '', caption: '' };
  const quality =
    ConfigurationService.getConfig('content').getSetting('imageQuality').value;

  const width = getMediaWidthFromElementWidth(
    media.featured ? elementWidth * 2 : elementWidth
  );

  const aspectRatio = getAspectRatioNumberFromEnum(media?.aspectRatio) ?? 1;

  const image = useMemo(() => {
    const src = new URL(media.item!.url);

    // All cloudinary images are coming from the builder.io plugin with http protocol.
    src.protocol = 'https:';

    const image: IImage = {
      uuid: media.item!.public_id,
      src: src.toString(),
      alt: metadata.alt,
      caption: metadata.caption,
      aspectRatio
    };

    const optimizedImage: IImage = MediaService.optimizeMedia(image, {
      width,
      fit: 'auto',
      quality,
      aspectRatio,
      gravity: media.gravity,
      omitNamedTransformation: true
    });

    return optimizedImage;
  }, [width, media, metadata.alt, quality, media?.item?.url, metadata.caption]);
  return (
    <div className={classes(S.container)} style={{ aspectRatio }}>
      <HoverManipulation media={media} hoverStyle={media.hoverStyle}>
        <Image image={image} fit={media.fit} useNext priority={highPriority} />
      </HoverManipulation>
    </div>
  );
};

export default ContentImage;
